import {getProxyList} from "@/redux/slices/mlSlices/chatSlice";

import {useCallback, useEffect, useRef} from "react";

export const useAvatarTransport = ({dispatch, proxyList}) => {
  const isProxySetupRef = useRef(false);
  const proxyListRef = useRef(proxyList);
  const originalFunctionsRef = useRef({
    fetch: null,
    xhrOpen: null,
    webSocket: null,
  });

  useEffect(() => {
    proxyListRef.current = proxyList;
  }, [proxyList]);

  const fetchProxyList = useCallback(async () => {
    try {
      await dispatch(getProxyList());
    } catch (error) {
      console.error("Error retrieving proxy list:", error);
    }
  }, [dispatch]);

  const getProxiedUrl = useCallback((url) => {
    try {
      const parsedUrl = new URL(url, window.location.origin);
      const isAbsolute =
        parsedUrl.protocol === "https:" ||
        parsedUrl.protocol === "http:" ||
        parsedUrl.protocol === "wss:" ||
        parsedUrl.protocol === "ws:";

      if (!isAbsolute) {
        return null;
      }

      if (parsedUrl.origin === window.location.origin) {
        return null;
      }

      const replacement = proxyListRef.current?.[parsedUrl.hostname];

      if (!replacement || replacement === parsedUrl.hostname) {
        return null;
      }

      const [targetHost, ...targetPathParts] = replacement.split("/");
      const targetBasePath = targetPathParts.join("/");
      const normalizedBasePath = targetBasePath
        ? `/${targetBasePath.replace(/^\/+|\/+$/g, "")}`
        : "";
      const originalPath = parsedUrl.pathname || "/";
      const proxiedPath =
        normalizedBasePath + (originalPath.startsWith("/") ? originalPath : `/${originalPath}`);

      parsedUrl.host = targetHost;
      parsedUrl.pathname = proxiedPath;

      const proxiedUrl = parsedUrl.toString();

      if (proxiedUrl !== url) {
        console.debug("[avatar-proxy]", {
          from: url,
          to: proxiedUrl,
          mappedHost: parsedUrl.hostname,
          targetHost,
          targetBasePath: normalizedBasePath || "/",
        });
      }

      return proxiedUrl;
    } catch (_error) {
      return null;
    }
  }, []);

  const isEmptyProxyList = useCallback((value) => {
    return (
      !value || typeof value !== "object" || Object.keys(value).length === 0
    );
  }, []);

  const setupProxyInterceptors = useCallback(() => {
    if (isProxySetupRef.current) return;
    if (isEmptyProxyList(proxyListRef.current)) return;

    isProxySetupRef.current = true;

    if (!originalFunctionsRef.current.fetch) {
      originalFunctionsRef.current.fetch = window.fetch;
    }

    window.fetch = async (...args) => {
      const originalUrl = args[0] instanceof Request ? args[0].url : args[0];
      const proxiedUrl =
        typeof originalUrl === "string" ? getProxiedUrl(originalUrl) : null;

      if (proxiedUrl) {
        args[0] =
          args[0] instanceof Request
            ? new Request(proxiedUrl, args[0])
            : proxiedUrl;
      }

      return originalFunctionsRef.current.fetch.apply(window, args);
    };

    if (!originalFunctionsRef.current.xhrOpen) {
      originalFunctionsRef.current.xhrOpen = XMLHttpRequest.prototype.open;
    }

    XMLHttpRequest.prototype.open = function (method, url, ...rest) {
      const proxiedUrl = typeof url === "string" ? getProxiedUrl(url) : null;

      return originalFunctionsRef.current.xhrOpen.apply(this, [
        method,
        proxiedUrl || url,
        ...rest,
      ]);
    };

    if (!originalFunctionsRef.current.webSocket) {
      originalFunctionsRef.current.webSocket = window.WebSocket;
    }

    const OriginalWebSocket = originalFunctionsRef.current.webSocket;

    window.WebSocket = class ProxiedWebSocket extends OriginalWebSocket {
      constructor(url, protocols) {
        const urlString = typeof url === "string" ? url : String(url);
        const proxiedUrl = getProxiedUrl(urlString);
        const targetUrl = proxiedUrl || url;

        if (typeof protocols === "undefined") {
          super(targetUrl);
        } else {
          super(targetUrl, protocols);
        }
      }
    };
  }, [getProxiedUrl, isEmptyProxyList]);

  const removeProxyInterceptors = useCallback(() => {
    if (!isProxySetupRef.current) return;

    if (originalFunctionsRef.current.fetch) {
      window.fetch = originalFunctionsRef.current.fetch;
      originalFunctionsRef.current.fetch = null;
    }

    if (originalFunctionsRef.current.xhrOpen) {
      XMLHttpRequest.prototype.open = originalFunctionsRef.current.xhrOpen;
      originalFunctionsRef.current.xhrOpen = null;
    }

    if (originalFunctionsRef.current.webSocket) {
      window.WebSocket = originalFunctionsRef.current.webSocket;
      originalFunctionsRef.current.webSocket = null;
    }

    isProxySetupRef.current = false;
  }, []);

  const enableTransportBoundary = useCallback(async () => {
    if (isEmptyProxyList(proxyListRef.current)) {
      await fetchProxyList();
    }
    setupProxyInterceptors();
  }, [fetchProxyList, isEmptyProxyList, setupProxyInterceptors]);

  const disableTransportBoundary = useCallback(() => {
    removeProxyInterceptors();
  }, [removeProxyInterceptors]);

  useEffect(() => {
    return () => {
      removeProxyInterceptors();
    };
  }, [removeProxyInterceptors]);

  return {
    enableTransportBoundary,
    disableTransportBoundary,
  };
};
